/**
 * A/B Test Detail View
 *
 * Detailed view for an A/B test including progress monitoring,
 * statistical results, conclude workflow, and add runs.
 * Follows RLLoopMonitor pattern for polling and layout.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as StartIcon,
  Cancel as CancelIcon,
  EmojiEvents as ConcludeIcon,
  Download as ExportIcon,
  AddCircle as AddRunsIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  ContentCopy as CloneIcon,
  Settings as SettingsIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import {
  getABTest,
  getABTestResults,
  startABTest,
  cancelABTest,
  concludeABTest,
  addABTestRuns,
  cloneABTest
} from '../../../services/advancedSimulationApi';

const STATUS_COLORS = {
  draft: 'default',
  running: 'primary',
  computing: 'warning',
  completed: 'success',
  concluded: 'info',
  cancelled: 'error'
};

const STATUS_LABELS = {
  draft: 'Draft',
  running: 'Running',
  computing: 'Computing Results...',
  completed: 'Completed',
  concluded: 'Concluded',
  cancelled: 'Cancelled'
};

const EFFECT_SIZE_COLORS = {
  negligible: 'default',
  small: 'info',
  medium: 'warning',
  large: 'error'
};

const VARIANT_COLORS = ['#1976d2', '#dc004e', '#388e3c', '#f57c00', '#7b1fa2'];

function ABTestDetailView({ testId, token, onClose, onExport, onClone, onEdit }) {
  const [test, setTest] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Conclude dialog
  const [concludeDialogOpen, setConcludeDialogOpen] = useState(false);
  const [concludeWinnerId, setConcludeWinnerId] = useState('');
  const [concludeNotes, setConcludeNotes] = useState('');
  const [concludeLoading, setConcludeLoading] = useState(false);

  // Add runs dialog
  const [addRunsDialogOpen, setAddRunsDialogOpen] = useState(false);
  const [additionalRuns, setAdditionalRuns] = useState(5);
  const [addRunsLoading, setAddRunsLoading] = useState(false);

  const fetchTest = useCallback(async () => {
    try {
      const response = await getABTest(testId, token);
      const testData = response.test || response;
      setTest(testData);

      // Fetch results if completed or concluded
      if (['completed', 'concluded'].includes(testData.status)) {
        try {
          const resultsResponse = await getABTestResults(testId, token);
          setResults(resultsResponse.test || resultsResponse);
        } catch (err) {
          console.error('Failed to load results:', err);
        }
      }
      setError(null);
    } catch (err) {
      setError(err.error || 'Failed to load test');
    } finally {
      setLoading(false);
    }
  }, [testId, token]);

  useEffect(() => {
    fetchTest();
  }, [fetchTest]);

  // Poll while running or computing
  useEffect(() => {
    if (!['running', 'computing'].includes(test?.status)) return;
    const interval = setInterval(fetchTest, 5000);
    return () => clearInterval(interval);
  }, [test?.status, fetchTest]);

  const handleStart = async () => {
    try {
      await startABTest(testId, token);
      fetchTest();
    } catch (err) {
      setError(err.error || 'Failed to start test');
    }
  };

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel this test?')) {
      try {
        await cancelABTest(testId, token);
        fetchTest();
      } catch (err) {
        setError(err.error || 'Failed to cancel test');
      }
    }
  };

  const handleConclude = async () => {
    setConcludeLoading(true);
    try {
      await concludeABTest(testId, { winnerId: concludeWinnerId, notes: concludeNotes }, token);
      setConcludeDialogOpen(false);
      fetchTest();
    } catch (err) {
      setError(err.error || 'Failed to conclude test');
    } finally {
      setConcludeLoading(false);
    }
  };

  const handleAddRuns = async () => {
    setAddRunsLoading(true);
    try {
      await addABTestRuns(testId, additionalRuns, token);
      setAddRunsDialogOpen(false);
      fetchTest();
    } catch (err) {
      setError(err.error || 'Failed to add runs');
    } finally {
      setAddRunsLoading(false);
    }
  };

  const handleClone = async () => {
    try {
      const result = await cloneABTest(testId, {}, token);
      const cloned = result.test || result;
      if (onClone) {
        onClone(cloned);
      }
    } catch (err) {
      setError(err.error || 'Failed to clone test');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!test) {
    return <Alert severity="error">Test not found</Alert>;
  }

  const totalExpectedRuns = (test.executionConfig?.runsPerVariantPerClient || 5)
    * (test.clientConfig?.clientPersonaKeys?.length || 1)
    * (test.variants?.length || 0);
  const totalCompletedRuns = (test.variants || []).reduce((sum, v) => sum + (v.stats?.runCount || 0), 0);
  const overallProgress = totalExpectedRuns > 0 ? Math.round((totalCompletedRuns / totalExpectedRuns) * 100) : 0;

  const testResults = results?.results || test?.results;

  // Build chart data for variant comparison (Recharts requires numeric values, not strings)
  const variantChartData = (test.variants || []).map((v, i) => ({
    name: v.name,
    avgQuality: parseFloat(v.stats?.avgQualityScore?.toFixed(1)) || 0,
    avgTurns: parseInt(v.stats?.avgTurns?.toFixed(0)) || 0,
    runCount: v.stats?.runCount || 0
  }));

  // Build dimension chart data
  const dimensionChartData = [];
  if (test.variants?.length > 0) {
    const allDimensions = new Set();
    test.variants.forEach(v => {
      (v.stats?.dimensionBreakdowns || []).forEach(d => allDimensions.add(d.dimension));
    });
    allDimensions.forEach(dim => {
      const label = dim.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const entry = { dimension: label };
      test.variants.forEach((v, i) => {
        const dimData = (v.stats?.dimensionBreakdowns || []).find(d => d.dimension === dim);
        entry[v.name] = parseFloat(dimData?.avgScore?.toFixed(2)) || 0;
      });
      dimensionChartData.push(entry);
    });
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h5">{test.name}</Typography>
            <Chip
              label={STATUS_LABELS[test.status] || test.status}
              color={STATUS_COLORS[test.status] || 'default'}
              size="small"
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {test.testType === 'prompt_version' ? 'Prompt Version Test' : 'Modality Comparison'}
            {test.hypothesis && ` | Hypothesis: ${test.hypothesis}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Confidence: {((test.executionConfig?.confidenceLevel || 0.95) * 100).toFixed(0)}%
            {test.startedAt && ` | Started: ${new Date(test.startedAt).toLocaleString()}`}
            {test.completedAt && ` | Completed: ${new Date(test.completedAt).toLocaleString()}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {test.status === 'draft' && onEdit && (
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => onEdit(test)}>
              Edit
            </Button>
          )}
          {test.status === 'draft' && (
            <Button size="small" variant="contained" startIcon={<StartIcon />} onClick={handleStart}>
              Start
            </Button>
          )}
          {['running', 'computing'].includes(test.status) && (
            <Button size="small" variant="outlined" color="warning" startIcon={<CancelIcon />} onClick={handleCancel}>
              Cancel
            </Button>
          )}
          {test.status === 'completed' && (
            <>
              <Button size="small" variant="contained" color="success" startIcon={<ConcludeIcon />}
                onClick={() => setConcludeDialogOpen(true)}>
                Conclude
              </Button>
              <Button size="small" variant="outlined" startIcon={<AddRunsIcon />}
                onClick={() => setAddRunsDialogOpen(true)}>
                Add Runs
              </Button>
            </>
          )}
          {['completed', 'concluded'].includes(test.status) && onExport && (
            <Button size="small" variant="outlined" startIcon={<ExportIcon />} onClick={() => onExport(test)}>
              Export
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<CloneIcon />} onClick={handleClone}>
            Clone
          </Button>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchTest}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Test Configuration */}
      <Accordion defaultExpanded={test.status === 'draft'} sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SettingsIcon fontSize="small" color="action" />
            <Typography variant="subtitle1">Test Configuration</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Test Setup */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Test Setup</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Test Type</Typography>
                  <Typography variant="body2">
                    {test.testType === 'prompt_version' ? 'Prompt Version' : 'Modality Comparison'}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Confidence Level</Typography>
                  <Typography variant="body2">
                    {((test.executionConfig?.confidenceLevel || 0.95) * 100).toFixed(0)}%
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Max Turns/Run</Typography>
                  <Typography variant="body2">{test.executionConfig?.maxTurnsPerRun || 50}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Parallelism</Typography>
                  <Typography variant="body2">{test.executionConfig?.parallelism || 3}</Typography>
                </Grid>
                {test.hypothesis && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Hypothesis</Typography>
                    <Typography variant="body2">{test.hypothesis}</Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Variant Configurations */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>Variant Configurations</Typography>
              <Grid container spacing={2}>
                {(test.variants || []).map((variant, idx) => (
                  <Grid item xs={12} sm={6} key={variant.variantId || idx}>
                    <Card variant="outlined">
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="subtitle2">{variant.name}</Typography>
                          {test.controlVariantId === variant.variantId && (
                            <Chip label="Control" size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                        {variant.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            {variant.description}
                          </Typography>
                        )}
                        {test.testType === 'modality_comparison' ? (
                          <>
                            <Typography variant="caption" color="text.secondary">Persona: </Typography>
                            <Typography variant="caption" fontWeight="medium">
                              {variant.therapistPersonaKey || '—'}
                            </Typography>
                            {variant.modality && (
                              <>
                                <br />
                                <Typography variant="caption" color="text.secondary">Modality: </Typography>
                                <Typography variant="caption" fontWeight="medium">{variant.modality}</Typography>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <Typography variant="caption" color="text.secondary">Base Persona: </Typography>
                            <Typography variant="caption" fontWeight="medium">
                              {variant.basePersonaKey || variant.therapistPersonaKey || '—'}
                            </Typography>
                            {variant.promptOverride && (
                              <Box sx={{ mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Prompt Override:</Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    bgcolor: 'grey.50',
                                    p: 0.5,
                                    borderRadius: 0.5,
                                    maxHeight: 80,
                                    overflow: 'auto',
                                    fontFamily: 'monospace',
                                    fontSize: '0.7rem',
                                    whiteSpace: 'pre-wrap'
                                  }}
                                >
                                  {variant.promptOverride}
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid item xs={12}><Divider /></Grid>

            {/* Client Configuration */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>Client Configuration</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {(test.clientConfig?.clientPersonaKeys || []).map((key) => (
                  <Chip key={key} label={key} size="small" variant="outlined" />
                ))}
                {(!test.clientConfig?.clientPersonaKeys || test.clientConfig.clientPersonaKeys.length === 0) && (
                  <Typography variant="caption" color="text.secondary">No client personas specified</Typography>
                )}
              </Box>
              {test.clientConfig?.conditionFilter?.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Condition filter: {test.clientConfig.conditionFilter.join(', ')}
                </Typography>
              )}
              {test.clientConfig?.severityFilter?.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Severity filter: {test.clientConfig.severityFilter.join(', ')}
                </Typography>
              )}
            </Grid>

            {/* Execution Settings */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" gutterBottom>Execution Settings</Typography>
              <Typography variant="caption" color="text.secondary">Runs per variant per client: </Typography>
              <Typography variant="caption" fontWeight="medium">
                {test.executionConfig?.runsPerVariantPerClient || 5}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">Max turns per run: </Typography>
              <Typography variant="caption" fontWeight="medium">
                {test.executionConfig?.maxTurnsPerRun || 50}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">Parallelism: </Typography>
              <Typography variant="caption" fontWeight="medium">
                {test.executionConfig?.parallelism || 3}
              </Typography>
              {test.executionConfig?.reviewerPersonaId && (
                <>
                  <br />
                  <Typography variant="caption" color="text.secondary">Reviewer persona: </Typography>
                  <Typography variant="caption" fontWeight="medium">
                    {test.executionConfig.reviewerPersonaId}
                  </Typography>
                </>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Progress Section (visible when running) */}
      {test.status === 'running' && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>Overall Progress</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress variant="determinate" value={overallProgress} sx={{ height: 8, borderRadius: 4 }} />
            </Box>
            <Typography variant="body2" fontWeight="medium">
              {overallProgress}%
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            {totalCompletedRuns} / {totalExpectedRuns} runs completed
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            {(test.variants || []).map((variant, idx) => {
              const variantExpected = (test.executionConfig?.runsPerVariantPerClient || 5)
                * (test.clientConfig?.clientPersonaKeys?.length || 1);
              const variantCompleted = variant.stats?.runCount || 0;
              const variantProgress = variantExpected > 0 ? Math.round((variantCompleted / variantExpected) * 100) : 0;

              return (
                <Grid item xs={12} sm={6} md={4} key={variant.variantId || idx}>
                  <Card variant="outlined">
                    <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" fontWeight="medium">{variant.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {variantCompleted}/{variantExpected}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={variantProgress}
                        sx={{ height: 4, borderRadius: 2 }}
                        color={idx === 0 ? 'primary' : 'secondary'}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Computing Progress Indicator */}
      {test.status === 'computing' && (
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="subtitle1" gutterBottom>Computing Results...</Typography>
          <Typography variant="body2" color="text.secondary">
            All {totalCompletedRuns} runs have completed. The system is now reviewing conversations
            and computing statistical results. This may take a few minutes.
          </Typography>
        </Paper>
      )}

      {/* Variant Comparison Cards */}
      {totalCompletedRuns > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1">Variant Statistics</Typography>
            {testResults?.reviewerPersonaName && (
              <Chip
                label={`Reviewed by: ${testResults.reviewerPersonaName}`}
                size="small"
                variant="outlined"
                color="info"
              />
            )}
          </Box>
          <Grid container spacing={2}>
            {(test.variants || []).map((variant, idx) => (
              <Grid item xs={12} sm={6} md={4} key={variant.variantId || idx}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">{variant.name}</Typography>
                      {test.controlVariantId === variant.variantId && (
                        <Chip label="Control" size="small" color="primary" variant="outlined" />
                      )}
                    </Box>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Runs</Typography>
                        <Typography variant="h6">{variant.stats?.runCount || 0}</Typography>
                        {variant.stats?.unscoredRuns > 0 && (
                          // Runs whose review failed are left out of the averages and tests (PD-09)
                          <Typography variant="caption" color="warning.main" display="block">
                            {variant.stats.unscoredRuns} unscored, not in the averages
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Avg Quality</Typography>
                        <Typography variant="h6" color="primary">
                          {variant.stats?.avgQualityScore?.toFixed(1) || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Std Dev</Typography>
                        <Typography variant="body2">
                          {variant.stats?.stdDevQuality?.toFixed(2) || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">Avg Turns</Typography>
                        <Typography variant="body2">
                          {variant.stats?.avgTurns?.toFixed(0) || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">Avg Cost</Typography>
                        <Typography variant="body2">
                          ${variant.stats?.avgCost?.toFixed(4) || '0'}
                        </Typography>
                      </Grid>
                    </Grid>

                    {/* Dimension breakdown */}
                    {variant.stats?.dimensionBreakdowns?.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          Clinical Dimensions
                        </Typography>
                        {variant.stats.dimensionBreakdowns.map((dim) => (
                          <Box key={dim.dimension} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption">
                              {dim.dimension.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </Typography>
                            <Typography variant="caption" fontWeight="medium">
                              {dim.avgScore?.toFixed(2)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Per-client breakdown */}
                    {variant.stats?.byClientPersona?.length > 0 && (
                      <Accordion sx={{ mt: 1 }} disableGutters elevation={0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 32, '& .MuiAccordionSummary-content': { my: 0 } }}>
                          <Typography variant="caption" color="text.secondary">
                            By Client Persona ({variant.stats.byClientPersona.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          {variant.stats.byClientPersona.map((cp) => (
                            <Box key={cp.clientPersonaKey} sx={{ display: 'flex', justifyContent: 'space-between', px: 1, py: 0.5 }}>
                              <Typography variant="caption">{cp.clientPersonaKey}</Typography>
                              <Typography variant="caption">
                                {cp.avgQualityScore?.toFixed(1)} ({cp.runCount} runs)
                              </Typography>
                            </Box>
                          ))}
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Charts */}
      {variantChartData.length > 0 && variantChartData.some(d => d.avgQuality > 0) && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Quality Comparison</Typography>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={variantChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <ChartTooltip />
              <Bar dataKey="avgQuality" fill="#1976d2" name="Avg Clinical Score" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {dimensionChartData.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Dimension Breakdown</Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dimensionChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="dimension" angle={-25} textAnchor="end" height={60} interval={0} tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 3]} label={{ value: 'Score (0-3)', angle: -90, position: 'insideLeft' }} />
              <ChartTooltip />
              <Legend />
              {(test.variants || []).map((v, i) => (
                <Bar key={v.name} dataKey={v.name} fill={VARIANT_COLORS[i % VARIANT_COLORS.length]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      )}

      {/* Statistical Results */}
      {testResults && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>Statistical Results</Typography>

          {/* Pairwise Comparisons */}
          {testResults.pairwiseComparisons?.length > 0 && (
            <Paper sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Pairwise Comparisons</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Comparison</TableCell>
                      <TableCell>Metric</TableCell>
                      <TableCell align="right">Mean A</TableCell>
                      <TableCell align="right">Mean B</TableCell>
                      <TableCell align="right">Diff</TableCell>
                      <TableCell align="right">p-value</TableCell>
                      <TableCell>Effect Size</TableCell>
                      <TableCell>CI</TableCell>
                      <TableCell>Significant?</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {testResults.pairwiseComparisons.map((comp, idx) => {
                      const alpha = 1 - (test.executionConfig?.confidenceLevel || 0.95);
                      return (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Typography variant="caption">
                              {comp.variantAName || 'A'} vs {comp.variantBName || 'B'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ textTransform: 'capitalize' }}>
                              {comp.metric}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{comp.meanA?.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{comp.meanB?.toFixed(2)}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color={comp.meanDifference > 0 ? 'success.main' : comp.meanDifference < 0 ? 'error.main' : 'text.primary'}>
                              {comp.meanDifference?.toFixed(3)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              color={comp.pValue < alpha ? 'success.main' : 'error.main'}
                            >
                              {comp.pValue?.toFixed(4)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={`${comp.effectSizeLabel || 'negligible'} (${comp.effectSize?.toFixed(2) || '?'})`}
                              size="small"
                              color={EFFECT_SIZE_COLORS[comp.effectSizeLabel] || 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              [{comp.confidenceInterval?.lower?.toFixed(3)}, {comp.confidenceInterval?.upper?.toFixed(3)}]
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {comp.isSignificant ? (
                                <CheckIcon fontSize="small" color="success" />
                              ) : (
                                <Typography variant="caption" color="text.secondary">No</Typography>
                              )}
                              {comp.sampleSizeSufficient === false && (
                                <Tooltip title="Sample size may be insufficient">
                                  <WarningIcon fontSize="small" color="warning" />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Rankings — Primary (Clinical) and Tiebreaker metrics */}
          {testResults.rankings?.length > 0 && (() => {
            const primaryRankings = testResults.rankings.filter(r => r.isPrimary);
            const tiebreakerRankings = testResults.rankings.filter(r => !r.isPrimary);

            return (
              <>
                {/* Primary Ranking: Clinical Effectiveness */}
                {primaryRankings.length > 0 && (
                  <Paper sx={{ p: 2, mb: 3, border: '2px solid', borderColor: 'primary.main' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="subtitle1">Primary: Clinical Effectiveness</Typography>
                      <Chip label="Primary Metric" size="small" color="primary" />
                      {testResults.reviewerPersonaName && (
                        <Chip
                          label={`Reviewer: ${testResults.reviewerPersonaName}`}
                          size="small"
                          variant="outlined"
                          color="info"
                        />
                      )}
                    </Box>
                    {primaryRankings.map((ranking, idx) => (
                      <Box key={idx}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Rank</TableCell>
                              <TableCell>Variant</TableCell>
                              <TableCell align="right">Mean Score</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(ranking.rankedVariants || []).map((rv) => (
                              <TableRow
                                key={rv.variantId}
                                sx={rv.rank === 1 ? { bgcolor: 'success.50', '& td': { fontWeight: 'bold' } } : {}}
                              >
                                <TableCell>
                                  {rv.rank === 1 ? (
                                    <Chip label="#1" size="small" color="success" />
                                  ) : (
                                    `#${rv.rank}`
                                  )}
                                </TableCell>
                                <TableCell>{rv.variantName}</TableCell>
                                <TableCell align="right">{rv.meanScore?.toFixed(1)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    ))}
                  </Paper>
                )}

                {/* Tiebreaker Metrics */}
                {tiebreakerRankings.length > 0 && (
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom color="text.secondary">
                      Tiebreaker Metrics
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                      Only relevant when clinical scores are tied or within margin of error
                    </Typography>
                    {tiebreakerRankings.map((ranking, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {ranking.label || ranking.metric}
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            ({ranking.lowerIsBetter ? 'lower is better' : 'higher is better'})
                          </Typography>
                        </Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Rank</TableCell>
                              <TableCell>Variant</TableCell>
                              <TableCell align="right">Mean Score</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(ranking.rankedVariants || []).map((rv) => (
                              <TableRow
                                key={rv.variantId}
                                sx={rv.rank === 1 ? { bgcolor: 'warning.50', '& td': { fontWeight: 'bold' } } : {}}
                              >
                                <TableCell>
                                  {rv.rank === 1 ? (
                                    <Chip label="#1" size="small" color="warning" />
                                  ) : (
                                    `#${rv.rank}`
                                  )}
                                </TableCell>
                                <TableCell>{rv.variantName}</TableCell>
                                <TableCell align="right">
                                  {ranking.metric === 'estimatedCost'
                                    ? `$${rv.meanScore?.toFixed(4)}`
                                    : rv.meanScore?.toFixed(1)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    ))}
                  </Paper>
                )}
              </>
            );
          })()}

          {/* LLM Comparative Analysis */}
          {testResults.comparativeAnalysis && (
            <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle1" gutterBottom>LLM Comparative Analysis</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {testResults.comparativeAnalysis}
              </Typography>
            </Paper>
          )}

          {/* By-Condition Breakdown */}
          {testResults.byCondition?.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">
                  By-Condition Breakdown ({testResults.byCondition.length} conditions)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {testResults.byCondition.map((cond, idx) => (
                  <Box key={idx} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {cond.condition}
                      {cond.bestVariantName && (
                        <Chip
                          label={`Best: ${cond.bestVariantName}`}
                          size="small"
                          color="success"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Variant</TableCell>
                          <TableCell align="right">Avg Quality</TableCell>
                          <TableCell align="right">Runs</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(cond.variantRankings || []).map((vr) => (
                          <TableRow key={vr.variantId}>
                            <TableCell>{vr.variantName}</TableCell>
                            <TableCell align="right">{vr.avgQualityScore?.toFixed(2)}</TableCell>
                            <TableCell align="right">{vr.runCount}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      )}

      {/* Conclusion Card */}
      {test.conclusion?.winnerId && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50', border: '2px solid', borderColor: 'success.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ConcludeIcon color="success" />
            <Typography variant="h6" color="success.main">Test Concluded</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Winner</Typography>
              <Typography variant="h6">{test.conclusion.winnerName || 'Unknown'}</Typography>
            </Grid>
            {test.conclusion.pValue != null && (
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">p-value</Typography>
                <Typography variant="body1">{test.conclusion.pValue.toFixed(4)}</Typography>
              </Grid>
            )}
            {test.conclusion.effectSize != null && (
              <Grid item xs={6} md={2}>
                <Typography variant="body2" color="text.secondary">Effect Size</Typography>
                <Typography variant="body1">{test.conclusion.effectSize.toFixed(3)}</Typography>
              </Grid>
            )}
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">Statistically Significant</Typography>
              <Typography variant="body1">
                {test.conclusion.isStatisticallySignificant ? 'Yes' : 'No'}
              </Typography>
            </Grid>
            {test.conclusion.notes && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Notes</Typography>
                <Typography variant="body2">{test.conclusion.notes}</Typography>
              </Grid>
            )}
            {test.conclusion.concludedAt && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Concluded at: {new Date(test.conclusion.concludedAt).toLocaleString()}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Conclude Dialog */}
      <Dialog open={concludeDialogOpen} onClose={() => setConcludeDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Conclude A/B Test</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Declare a winner based on the statistical results. This action is final.
          </Typography>

          {/* Show significance status */}
          {testResults?.pairwiseComparisons?.length > 0 && (
            <Alert
              severity={testResults.pairwiseComparisons.some(c => c.isSignificant) ? 'success' : 'warning'}
              sx={{ mb: 2 }}
            >
              {testResults.pairwiseComparisons.some(c => c.isSignificant)
                ? 'Statistically significant differences were found between variants.'
                : 'No statistically significant differences were found. Consider adding more runs.'}
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Winner</InputLabel>
            <Select
              value={concludeWinnerId}
              onChange={(e) => setConcludeWinnerId(e.target.value)}
              label="Winner"
            >
              {(test.variants || []).map((v) => (
                <MenuItem key={v.variantId} value={v.variantId}>
                  {v.name} (Avg: {v.stats?.avgQualityScore?.toFixed(1) || '—'})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Notes"
            value={concludeNotes}
            onChange={(e) => setConcludeNotes(e.target.value)}
            placeholder="Rationale for choosing this winner..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConcludeDialogOpen(false)} disabled={concludeLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConclude}
            disabled={!concludeWinnerId || concludeLoading}
            startIcon={concludeLoading ? <CircularProgress size={20} /> : <ConcludeIcon />}
          >
            {concludeLoading ? 'Concluding...' : 'Conclude Test'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Runs Dialog */}
      <Dialog open={addRunsDialogOpen} onClose={() => setAddRunsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add More Runs</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Run additional simulations per variant to improve statistical power.
          </Typography>

          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">Current sample sizes:</Typography>
            {(test.variants || []).map((v) => (
              <Typography variant="body2" key={v.variantId || v.name}>
                {v.name}: {v.stats?.runCount || 0} runs
              </Typography>
            ))}
          </Box>

          {testResults?.pairwiseComparisons?.some(c => !c.sampleSizeSufficient) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Some comparisons may have insufficient sample sizes for reliable results.
            </Alert>
          )}

          <TextField
            fullWidth
            type="number"
            label="Additional Runs per Variant"
            value={additionalRuns}
            onChange={(e) => setAdditionalRuns(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
            inputProps={{ min: 1, max: 20 }}
            helperText="Each variant will run this many additional simulations"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddRunsDialogOpen(false)} disabled={addRunsLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddRuns}
            disabled={addRunsLoading}
            startIcon={addRunsLoading ? <CircularProgress size={20} /> : <AddRunsIcon />}
          >
            {addRunsLoading ? 'Adding...' : `Add ${additionalRuns} Runs per Variant`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ABTestDetailView;
